import React from 'react'

import { cn } from '../ui/cn'
import { Wrapper } from '../ui/Wrapper'

const ImgBlock = Wrapper
const CopyBlock = Wrapper

interface SplitBlockProps {
  imgUrl?: string
  imgSide?: 'left' | 'right'
  imgAlt?: string
  companion?: React.ReactNode
  children?: React.ReactNode
  className?: string
}

/**
 * Two-column image + copy split. Matches playground `SplitBlock` exactly —
 * stacked on mobile (grid-cols-1), 6/5 columns on md+ with img side-swap.
 * Image renders as an aspect-video hero on mobile; on md+ it fills the block
 * absolutely so companion overlays can escape the aspect ratio.
 */
export const SplitBlock = ({
  imgUrl,
  imgSide = 'right',
  imgAlt,
  companion,
  children,
  className,
}: SplitBlockProps): React.ReactElement => {
  return (
    <section
      className={cn(
        'snplay-bounds snplay:my-24 snplay:grid snplay:grid-cols-1',
        'snplay:items-stretch snplay:gap-8 snplay:md:grid-cols-12',
        className,
      )}
    >
      <CopyBlock
        className={cn(
          'snplay:order-3 snplay:col-span-1 snplay:self-center',
          'snplay:md:col-span-5 snplay:md:py-20',
          imgSide === 'left' && 'snplay:md:col-start-8',
        )}
      >
        {children}
      </CopyBlock>
      <ImgBlock
        className={cn(
          'snplay:relative snplay:col-span-1 snplay:flex snplay:flex-col',
          'snplay:md:col-span-6 snplay:md:min-h-136',
          imgSide === 'right' && 'snplay:md:order-5 snplay:md:col-start-7',
          imgSide === 'left' && 'snplay:md:order-1',
          companion && 'snplay:max-md:mb-4',
        )}
      >
        {companion}
        <img
          src={imgUrl}
          alt={imgAlt}
          className={cn(
            'snplay:aspect-video snplay:w-full snplay:rounded-2xl snplay:object-cover',
            'snplay:md:absolute snplay:md:inset-0 snplay:md:aspect-auto snplay:md:h-full',
          )}
        />
      </ImgBlock>
    </section>
  )
}
